import React from 'react';
import memoize, { type MemoizedFn } from 'memoize-one';
import { dequal } from 'dequal/lite';
import { type ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import {
	type AnalyticsWebClient,
	type ForgeUIAnalyticsContext,
	sendEvent,
	extensionIdToAnalyticsAttributes,
} from '@atlassian/forge-ui';
import {
	type ContextId,
	type ProductEnvironment,
	type ForgeUIExtensionType,
} from '@atlassian/forge-ui-types';
import { type ExtensionHandlerWithReferenceFn } from '@atlassian/editor-referentiality';
import { type ReferenceEntity, type ExtensionHandlers } from '@atlaskit/editor-common/extensions';
import { OPERATIONAL_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import ForgeEditorExtension from './ForgeEditorExtension';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	type ConnectToForgeParameters,
	type CustomEditContext,
	type ForgeExtension,
	type ForgeExtensionParameters,
	type EditorAPI,
} from '../types';
import {
	P2_OR_CONNECT_CONFLUENCE_MACRO_EXTENSION_TYPE,
	CONNECT_ADDON_ACTION_SUBJECT,
} from '../utils/constants';
import { type FlagFunctions } from '../utils/getFlagProvider';
import { getConnectMacroId } from '../utils/getConnectMacroId';

interface GetExtensionParams {
	node: ForgeExtension;
	references?: Array<ReferenceEntity>;
}
interface RenderExtension {
	accountId?: string;
	analyticsWebClient: AnalyticsWebClient | Promise<AnalyticsWebClient>;
	cloudId: string;
	contextIds: ContextId[];
	customEditContext?: CustomEditContext;
	dataProviders: ProviderFactory;
	editorAPI?: EditorAPI;
	environment: ProductEnvironment;
	extension?: ForgeUIExtensionType;
	extensionData: Record<string, any>;
	extensionHandlerWithReference?: ExtensionHandlerWithReferenceFn<ForgeExtensionParameters>;
	FlagProvider: ({ children }: { children: (flags: FlagFunctions) => JSX.Element }) => JSX.Element;
	forgeMacroExtensions?: readonly ForgeUIExtensionType[] | null;
	forgeUIAnalyticsContext: ForgeUIAnalyticsContext;
	hideGlassPane?: boolean;
	isEditing: boolean;
	page: string;
	product: string;
	productExtensionHandlers?: ExtensionHandlers;
	useGetContextToken?: (params: {
		cachingEnabled?: boolean;
		contextIds: string[];
		extension: ForgeUIExtensionType | undefined;
		extensionData: RenderExtension['extensionData'];
		localId: string | undefined;
	}) => (contextIdsOverride?: string[]) => Promise<string>;
	useRouteActions?: () => {
		[key: string]: (...args: any[]) => any;
		push: (url: string, forceReload?: boolean) => void;
	};
}

type ContextTokenHandlerWrapperProps = {
	children: (
		getContextTokenHandler: (contextIdsOverride?: string[]) => Promise<string>,
	) => JSX.Element;
	contextIds: string[];
	extension: ForgeUIExtensionType | undefined;
	extensionData: RenderExtension['extensionData'];
	localId: string | undefined;
	useGetContextToken: (params: {
		cachingEnabled?: boolean;
		contextIds: string[];
		extension: ForgeUIExtensionType | undefined;
		extensionData: RenderExtension['extensionData'];
		localId: string | undefined;
	}) => (contextIdsOverride?: string[]) => Promise<string>;
};

function ContextTokenHandlerWrapper({
	useGetContextToken,
	extension,
	extensionData,
	localId,
	contextIds,
	children,
}: ContextTokenHandlerWrapperProps) {
	const getContextTokenHandler = useGetContextToken({
		extension,
		extensionData,
		localId,
		contextIds,
		cachingEnabled: true,
	});
	return children(getContextTokenHandler);
}

export const renderExtension = ({
	accountId,
	analyticsWebClient,
	cloudId,
	contextIds,
	environment,
	extension,
	extensionData,
	extensionHandlerWithReference,
	forgeMacroExtensions,
	forgeUIAnalyticsContext,
	isEditing,
	page,
	product,
	dataProviders,
	FlagProvider,
	hideGlassPane,
	editorAPI,
	customEditContext,
	useGetContextToken,
	useRouteActions,
	productExtensionHandlers,
}: RenderExtension): MemoizedFn<({ node, references }: GetExtensionParams) => JSX.Element> =>
	memoize(({ node, references }: GetExtensionParams) => {
		// Some nodes come through with a malformed `extensionId` where an extra segment
		// (e.g. `:1:`) is present between the colons of the ARI prefix, i.e.
		// `ari:cloud:ecosystem:1:extension/...` instead of the correct
		// `ari:cloud:ecosystem::extension/...`. Downstream consumers parse this value with
		// `@atlassian/cs-ari`'s `parse()`, which throws on the malformed form. Normalise it
		// here, before rendering, so the extension can still be rendered.
		if (fg('forge-ui-normalize-malformed-extension-id')) {
			normalizeMalformedExtensionId(node.parameters);
		}

		const extensionWithParam = {
			...node,
			parameters: {
				// we always insert with parameters
				...node.parameters!,
				extensionTitle: extension?.properties.title,
				extension,
			},
		};

		// If `localId` doesn't exist at this point, pass the `localId` on the node created from `quickInsert` into the parameters object
		// Required to render the correct config sidebar panel
		if (node.parameters && !node.parameters.localId) {
			node.parameters.localId = node.localId as string;
		}

		// moves specific macro extension data (isEditing, macro) to extensionData instead of extensionPayload as this is required for Tempo (#hot-300905)
		// the first check enables the feature for Tempo specifically, the second check enables the feature for all extensions if needed
		const enabledForTempo =
			extension?.appId === '76206a70-b9d6-4328-9e20-db79196c548a' &&
			fg('forge-ui-move-macro-data-for-tempo');
		const shouldIncludeMacroDataInExtensionData =
			enabledForTempo || fg('forge-ui-move-macro-data-to-extension-data');

		const isBodiedMacro = extension?.type === 'bodiedExtension';
		const macroExtensionData = {
			...(customEditContext
				? {
						isConfiguring: true,
						isInserting: customEditContext.isInserting,
					}
				: {}),
			...(isBodiedMacro ? { layout: 'bodied' } : {}),
		};

		const augmentedExtensionData: RenderExtension['extensionData'] =
			shouldIncludeMacroDataInExtensionData
				? {
						...extensionData,
						autoConvertLink: node.parameters?.autoConvertLink,
						isEditing,
						...(Object.keys(macroExtensionData).length ? { macro: macroExtensionData } : {}),
					}
				: {
						...extensionData,
						autoConvertLink: node.parameters?.autoConvertLink,
					};

		const connectMetadata = getConnectMacroMetadata(node.parameters!);
		if (connectMetadata) {
			augmentedExtensionData.connectMetadata = connectMetadata;
		}

		const getForgeEditorExtensionComponent = ({ references }: GetExtensionParams) => (
			<FlagProvider>
				{(flags: FlagFunctions) =>
					useGetContextToken ? (
						<ContextTokenHandlerWrapper
							useGetContextToken={useGetContextToken}
							extension={extensionWithParam?.parameters?.extension}
							extensionData={augmentedExtensionData}
							localId={node.parameters?.localId}
							contextIds={contextIds}
						>
							{(getContextTokenHandler) => (
								<ForgeEditorExtension
									analyticsWebClient={analyticsWebClient}
									accountId={accountId || ''}
									cloudId={cloudId}
									contextIds={contextIds}
									environment={environment}
									extension={transformConnectToForgeExtension(extensionWithParam, extension, {
										analyticsWebClient,
										forgeUIAnalyticsContext,
										isEditing,
									})}
									extensionData={{
										...augmentedExtensionData,
										references,
									}}
									forgeMacroExtensions={forgeMacroExtensions}
									flags={flags}
									isEditing={isEditing}
									product={product}
									dataProviders={dataProviders}
									page={page}
									hideGlassPane={hideGlassPane}
									editorAPI={editorAPI}
									getContextToken={getContextTokenHandler}
									customEditContext={customEditContext}
									useRouteActions={useRouteActions}
									productExtensionHandlers={productExtensionHandlers}
								/>
							)}
						</ContextTokenHandlerWrapper>
					) : (
						<ForgeEditorExtension
							analyticsWebClient={analyticsWebClient}
							accountId={accountId || ''}
							cloudId={cloudId}
							contextIds={contextIds}
							environment={environment}
							extension={transformConnectToForgeExtension(extensionWithParam, extension, {
								analyticsWebClient,
								forgeUIAnalyticsContext,
								isEditing,
							})}
							extensionData={{
								...augmentedExtensionData,
								references,
							}}
							forgeMacroExtensions={forgeMacroExtensions}
							flags={flags}
							isEditing={isEditing}
							product={product}
							dataProviders={dataProviders}
							page={page}
							hideGlassPane={hideGlassPane}
							editorAPI={editorAPI}
							customEditContext={customEditContext}
							useRouteActions={useRouteActions}
							productExtensionHandlers={productExtensionHandlers}
						/>
					)
				}
			</FlagProvider>
		);

		if (extensionHandlerWithReference) {
			return extensionHandlerWithReference(node, ({ references: referencesFromHandler }) =>
				getForgeEditorExtensionComponent({
					node,
					references: referencesFromHandler,
				}),
			);
		}

		return getForgeEditorExtensionComponent({ node, references });
	}, dequal);

// The correct Forge extension ARI prefix. Note the empty segment (`::`) for the
// unused `siteId` part of the ARI.
const VALID_EXTENSION_ID_PREFIX = 'ari:cloud:ecosystem::extension/';
// The malformed prefix that has an extra segment (e.g. `1`) where the ARI expects an
// empty segment, i.e. `ari:cloud:ecosystem:1:extension/`.
const MALFORMED_EXTENSION_ID_PREFIX_PATTERN = /^ari:cloud:ecosystem:[^:]+:extension\//;

/**
 * Fixes a malformed `extensionId` on the given extension parameters in place.
 *
 * Some nodes arrive with an `extensionId` such as
 * `ari:cloud:ecosystem:1:extension/...` where an extra segment is present between the
 * colons of the ARI prefix. The correct prefix is `ari:cloud:ecosystem::extension/`.
 * This normalises the value so downstream ARI parsing does not fail.
 */
export function normalizeMalformedExtensionId(
	parameters: ForgeExtension['parameters'] | undefined,
): void {
	const extensionId = parameters?.extensionId;
	if (
		typeof extensionId === 'string' &&
		!extensionId.startsWith(VALID_EXTENSION_ID_PREFIX) &&
		MALFORMED_EXTENSION_ID_PREFIX_PATTERN.test(extensionId)
	) {
		parameters!.extensionId = extensionId.replace(
			MALFORMED_EXTENSION_ID_PREFIX_PATTERN,
			VALID_EXTENSION_ID_PREFIX,
		);
	}
}

function getConnectMacroMetadata(parameters: ConnectToForgeParameters):
	| {
			macroId: string;
			title?: string;
	  }
	| undefined {
	const macroId = getConnectMacroId(parameters);
	const title = parameters?.macroMetadata?.title;
	if (!macroId) {
		// We don't want this function to return anything if it didn't have a Connect macro ID,
		// to avoid implying that pure Forge macros were Connect
		return undefined;
	}
	return {
		macroId,
		title,
	};
}

function transformConnectToForgeExtension(
	attrs: ForgeExtension,
	extension?: ForgeUIExtensionType,
	analyticsProps?: Pick<
		RenderExtension,
		'analyticsWebClient' | 'forgeUIAnalyticsContext' | 'isEditing'
	>,
): ForgeExtension {
	if (attrs.extensionType !== P2_OR_CONNECT_CONFLUENCE_MACRO_EXTENSION_TYPE) {
		return attrs;
	}

	const { analyticsWebClient, forgeUIAnalyticsContext, isEditing } = analyticsProps || {};
	const eventSource = isEditing ? 'editPageScreen' : 'viewPageScreen';
	const { extensionKey } = attrs;

	try {
		if (!extension?.id) {
			throw new Error('No Forge extension for patching connect addon');
		}
		if (attrs.parameters) {
			if (!attrs.parameters.extensionId) {
				attrs.parameters.extensionId = extension.id;
			}
			const macroId = getConnectMacroId(attrs.parameters);
			if (!attrs.parameters.localId && macroId) {
				// Populate with Connect macroId
				attrs.parameters.localId = macroId;
			}
		}

		// Transform Connect macroParams to Forge guestParams
		if (attrs.parameters?.macroParams) {
			attrs.parameters.guestParams = attrs.parameters.guestParams || {};
			const connectParams = attrs.parameters.macroParams;
			Object.entries(connectParams).forEach(([key, obj]: [string, any]) => {
				if (typeof attrs.parameters!.guestParams![key] === 'undefined') {
					attrs.parameters!.guestParams![key] = obj.value;
				}
			});
		}
		if (analyticsWebClient) {
			sendEvent(analyticsWebClient)({
				source: eventSource,
				...forgeUIAnalyticsContext,
				eventType: OPERATIONAL_EVENT_TYPE,
				action: 'h11n_macro_render_success',
				actionSubject: CONNECT_ADDON_ACTION_SUBJECT,
				actionSubjectId: extensionKey,
				attributes: {
					extensionKey,
					...extensionIdToAnalyticsAttributes(extension?.id),
					nodeLocalId: attrs.localId,
				},
			});
		}
	} catch (e) {
		if (analyticsWebClient) {
			sendEvent(analyticsWebClient)({
				source: eventSource,
				...forgeUIAnalyticsContext,
				eventType: OPERATIONAL_EVENT_TYPE,
				action: 'h11n_macro_render_failed',
				actionSubject: CONNECT_ADDON_ACTION_SUBJECT,
				actionSubjectId: extensionKey,
				attributes: {
					extensionKey,
					...extensionIdToAnalyticsAttributes(extension?.id),
					errorMessage: e instanceof Error ? e.message : String(e),
					nodeLocalId: attrs.localId,
				},
			});
		}
		throw e;
	}

	return attrs;
}
